import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaArrowLeft, FaGooglePlay, FaDownload, FaPhone, FaEnvelope, FaShieldAlt, FaCloud, FaChartLine, FaBell, FaCar, FaGasPump, FaWrench, FaCheckCircle, FaStar } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { apps } from '../data';
import { useTheme } from '../contexts/ThemeContext';

const CarMaintenanceApp: React.FC = () => {
  const navigate = useNavigate();
  const { theme, themeClasses } = useTheme();
  const app = apps.find(a => a.id === '2');
  
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  if (!app || !app.detailedInfo) {
    return null;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const featureIcons: Record<string, React.ReactNode> = {
    '📊': <FaChartLine className="w-6 h-6" />,
    '⛽': <FaGasPump className="w-6 h-6" />,
    '🔔': <FaBell className="w-6 h-6" />,
    '🚙': <FaCar className="w-6 h-6" />,
    '📈': <FaChartLine className="w-6 h-6" />,
    '☁️': <FaCloud className="w-6 h-6" />
  };

  return (
    <div className={`min-h-screen ${themeClasses.bg} pt-20`}>
      {/* Header with Back Button */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 ${themeClasses.bg}/90 backdrop-blur-md border-b ${themeClasses.border}/20`}
      >
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className={`flex items-center space-x-2 ${themeClasses.text} hover:opacity-80 transition-opacity`}
            >
              <FaArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Back to Portfolio</span>
            </motion.button>
            <div className="flex items-center space-x-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                theme === 'slate' ? 'bg-teal-400/20 text-teal-400' :
                theme === 'midnight' ? 'bg-sky-400/20 text-sky-400' :
                theme === 'studio' ? 'bg-teal-700/20 text-teal-700' :'bg-sky-400/20 text-sky-400'
              }`}>
                {app.status}
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="pt-8 pb-16 sm:pt-12 sm:pb-20">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <motion.div
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="text-center mb-12"
          >
            <motion.div variants={itemVariants} className="mb-6">
              <div className="text-8xl mb-6">{app.icon}</div>
              <h1 className="text-5xl sm:text-6xl font-bold mb-4">
                <span className={`bg-gradient-to-r ${themeClasses.gradient} bg-clip-text text-transparent`}>
                  {app.name}
                </span>
              </h1>
              <p className={`text-xl sm:text-2xl ${themeClasses.textMuted} max-w-3xl mx-auto leading-relaxed`}>
                {app.description}
              </p>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="flex flex-wrap items-center justify-center gap-4 mb-8"
            >
              {app.downloadUrl && (
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href={app.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center space-x-3 px-6 py-4 rounded-lg bg-gradient-to-r ${themeClasses.gradient} text-white font-semibold shadow-lg hover:shadow-xl transition-all`}
                >
                  <FaGooglePlay className="w-6 h-6" />
                  <span>Download on Google Play</span>
                </motion.a>
              )}
              <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg border ${themeClasses.border}/30 ${themeClasses.textMuted}`}>
                <FaStar className={`w-5 h-5 text-yellow-400`} />
                <span>{app.detailedInfo.rating} Rating</span>
              </div>
              <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg border ${themeClasses.border}/30 ${themeClasses.textMuted}`}>
                <FaDownload className="w-5 h-5" />
                <span>{app.detailedInfo.downloads} Downloads</span>
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className={`flex flex-wrap justify-center gap-4 text-sm ${themeClasses.textSoft}`}
            >
              <span>Platform: {app.platform}</span>
              <span>•</span>
              <span>Category: {app.detailedInfo.category}</span>
              <span>•</span>
              <span>Content Rating: {app.detailedInfo.contentRating}</span>
            </motion.div>
          </motion.div>

          {/* Key Features */}
          <motion.div
            variants={containerVariants}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16"
          >
            {app.detailedInfo.keyFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02, y: -5 }}
                className={`p-6 rounded-xl border ${themeClasses.card} hover:${themeClasses.border}/40 transition-all`}
              >
                <div className={`${theme === 'slate' ? 'text-teal-400' : theme === 'midnight' ? 'text-sky-400' : theme === 'studio' ? 'text-teal-700' : 'text-sky-400'} mb-4`}>
                  {featureIcons[feature.icon] || <FaWrench className="w-6 h-6" />}
                </div>
                <h3 className={`text-xl font-bold mb-3 ${themeClasses.text}`}>
                  {feature.title.replace(/^[^\s]+\s/, '')}
                </h3>
                <p className={`${themeClasses.textMuted} leading-relaxed`}>
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Detailed Description */}
          <motion.div
            variants={itemVariants}
            className={`p-8 rounded-xl border ${themeClasses.card} mb-12`}
          >
            <h2 className={`text-3xl font-bold mb-6 ${themeClasses.text}`}>
              About Auto Maintenance
            </h2>
            <div className={`space-y-4 ${themeClasses.textMuted} leading-relaxed`}>
              <p>
                <strong className={themeClasses.text}>Auto Maintenance</strong> is your complete vehicle care companion that helps you track service history, monitor expenses, and never miss important maintenance. Whether you drive a car, truck, motorcycle, or any vehicle - keep everything organized in one place!
              </p>
              <p>
                Perfect for car owners who want to maintain their vehicle properly, fleet managers tracking multiple vehicles, anyone wanting to track vehicle expenses for tax purposes, DIY mechanics logging their own repairs, and anyone who wants to increase vehicle resale value with complete service history.
              </p>
            </div>
          </motion.div>

          {/* Premium Features */}
          <motion.div
            variants={itemVariants}
            className={`p-8 rounded-xl border ${themeClasses.card} mb-12 ${
              theme === 'slate' ? 'bg-gradient-to-br bg-teal-400/10' :
              theme === 'midnight' ? 'bg-gradient-to-br bg-sky-400/10' :
              theme === 'studio' ? 'bg-gradient-to-br bg-teal-700/10' :'bg-gradient-to-br bg-sky-400/10'
            }`}
          >
            <h2 className={`text-3xl font-bold mb-6 ${themeClasses.text}`}>
              💎 Premium Features
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {app.detailedInfo.premiumFeatures.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <FaCheckCircle className={`w-5 h-5 mt-1 flex-shrink-0 ${
                    theme === 'slate' ? 'text-teal-400' :
                    theme === 'midnight' ? 'text-sky-400' :
                    theme === 'studio' ? 'text-teal-700' :'text-sky-400'
                  }`} />
                  <span className={themeClasses.text + '/80'}>{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Benefits */}
          <motion.div
            variants={itemVariants}
            className={`p-8 rounded-xl border ${themeClasses.card} mb-12`}
          >
            <h2 className={`text-3xl font-bold mb-6 ${themeClasses.text}`}>
              💰 Save Money
            </h2>
            <p className={`${themeClasses.textMuted} mb-6 leading-relaxed`}>
              By tracking maintenance properly, you can:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {app.detailedInfo.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                    theme === 'slate' ? 'bg-teal-400' :
                    theme === 'midnight' ? 'bg-sky-400' :
                    theme === 'studio' ? 'bg-teal-700' :'bg-sky-400'
                  }`}></div>
                  <span className={themeClasses.text + '/80'}>{benefit}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Privacy & Security */}
          <motion.div
            variants={itemVariants}
            className={`p-8 rounded-xl border ${themeClasses.card} mb-12`}
          >
            <div className="flex items-center space-x-3 mb-6">
              <FaShieldAlt className={`w-8 h-8 ${
                theme === 'slate' ? 'text-teal-400' :
                theme === 'midnight' ? 'text-sky-400' :
                theme === 'studio' ? 'text-teal-700' :'text-sky-400'
              }`} />
              <h2 className={`text-3xl font-bold ${themeClasses.text}`}>
                🔒 Privacy & Security
              </h2>
            </div>
            <p className={`${themeClasses.textMuted} mb-6 leading-relaxed`}>
              Your data is secure and private:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {app.detailedInfo.privacy.map((item, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <FaCheckCircle className={`w-5 h-5 mt-1 flex-shrink-0 ${
                    theme === 'slate' ? 'text-teal-400' :
                    theme === 'midnight' ? 'text-sky-400' :
                    theme === 'studio' ? 'text-teal-700' :'text-sky-400'
                  }`} />
                  <span className={themeClasses.text + '/80'}>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Technologies Used */}
          <motion.div
            variants={itemVariants}
            className={`p-8 rounded-xl border ${themeClasses.card} mb-12`}
          >
            <h2 className={`text-3xl font-bold mb-6 ${themeClasses.text}`}>
              🛠️ Technologies Used
            </h2>
            <div className="flex flex-wrap gap-3">
              {app.technologies.map((tech, index) => (
                <span
                  key={index}
                  className={`px-4 py-2 rounded-full border ${themeClasses.border}/30 ${themeClasses.textMuted} ${
                    theme === 'slate' ? 'bg-teal-400/10' :
                    theme === 'midnight' ? 'bg-sky-400/10' :
                    theme === 'studio' ? 'bg-teal-700/10' :'bg-sky-400/10'
                  }`}
                >
                  {tech}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Contact Support */}
          <motion.div
            variants={itemVariants}
            className={`p-8 rounded-xl border ${themeClasses.card} mb-12`}
          >
            <h2 className={`text-3xl font-bold mb-6 ${themeClasses.text}`}>
              📧 Support
            </h2>
            <p className={`${themeClasses.textMuted} mb-6 leading-relaxed`}>
              Need help? We're committed to making Auto Maintenance the best vehicle tracking app available. Your feedback helps us improve!
            </p>
            <div className="flex flex-wrap gap-4">
              <motion.a
                whileHover={{ scale: 1.05 }}
                href={`mailto:${app.detailedInfo.supportEmail}`}
                className={`flex items-center space-x-3 px-6 py-3 rounded-lg border ${themeClasses.border} ${themeClasses.text} transition-all ${
                  theme === 'slate' ? 'hover:bg-teal-400/10' :
                  theme === 'midnight' ? 'hover:bg-sky-400/10' :
                  theme === 'studio' ? 'hover:bg-teal-700/10' :
                  'hover:bg-sky-400/10'
                }`}
              >
                <FaEnvelope className="w-5 h-5" />
                <span>{app.detailedInfo.supportEmail}</span>
              </motion.a>
              {app.detailedInfo.phoneNumber && (
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  href={`tel:${app.detailedInfo.phoneNumber}`}
                  className={`flex items-center space-x-3 px-6 py-3 rounded-lg border ${themeClasses.border} ${themeClasses.text} transition-all ${
                  theme === 'slate' ? 'hover:bg-teal-400/10' :
                  theme === 'midnight' ? 'hover:bg-sky-400/10' :
                  theme === 'studio' ? 'hover:bg-teal-700/10' :
                  'hover:bg-sky-400/10'
                }`}
                >
                  <FaPhone className="w-5 h-5" />
                  <span>{app.detailedInfo.phoneNumber}</span>
                </motion.a>
              )}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            variants={itemVariants}
            className={`p-8 rounded-xl border ${themeClasses.card} text-center ${
              theme === 'slate' ? 'bg-gradient-to-br bg-teal-400/10' :
              theme === 'midnight' ? 'bg-gradient-to-br bg-sky-400/10' :
              theme === 'studio' ? 'bg-gradient-to-br bg-teal-700/10' :'bg-gradient-to-br bg-sky-400/10'
            }`}
          >
            <h2 className={`text-3xl font-bold mb-4 ${themeClasses.text}`}>
              🚀 Get Started Today
            </h2>
            <p className={`${themeClasses.textMuted} mb-8 max-w-2xl mx-auto leading-relaxed`}>
              Download Auto Maintenance now and take control of your vehicle care. Whether you're tracking one car or managing a fleet, we make it simple to stay organized and keep your vehicles in top condition.
            </p>
            {app.downloadUrl && (
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={app.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center space-x-3 px-8 py-4 rounded-lg bg-gradient-to-r ${themeClasses.gradient} text-white font-semibold shadow-lg hover:shadow-xl transition-all text-lg`}
              >
                <FaGooglePlay className="w-7 h-7" />
                <span>Download Now</span>
              </motion.a>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CarMaintenanceApp;

